import React from "react";
import { ChevronDown, Check } from "lucide-react";

const SIZE = {
    xs: "px-2 py-1 text-xs gap-1",
    sm: "px-3 py-1.5 text-sm gap-1.5",
    md: "px-3 py-2 text-sm gap-1.5",
    lg: "px-4 py-2.5 text-base gap-2",
};

/**
 * SelectDropdown — select personalizado del design system.
 *
 * Props:
 *   options     — array de { value, label, icon? }
 *   value       — valor seleccionado actualmente
 *   onChange    — (value) => void
 *   placeholder — texto cuando no hay selección (default: "Seleccionar…")
 *   size        — "xs" | "sm" | "md" | "lg" (default: "md")
 *   className   — clases extra para el wrapper
 *   disabled    — deshabilita el trigger
 */
export function SelectDropdown({
    options = [],
    value,
    onChange,
    placeholder = "Seleccionar…",
    size = "md",
    className = "",
    disabled = false,
}) {
    const selected = options.find((o) => o.value === value);

    return (
        <div className={`dropdown ${className}`}>
            <label
                tabIndex={disabled ? undefined : 0}
                className={`flex items-center w-full rounded-lg font-medium select-none
                    border border-base-content/20 bg-base-100 text-base-content
                    transition-colors duration-150
                    ${disabled
                        ? "opacity-40 pointer-events-none cursor-not-allowed"
                        : "cursor-pointer hover:bg-base-200"
                    }
                    ${SIZE[size] ?? SIZE.md}`}
            >
                {selected?.icon && (
                    <span className="shrink-0 text-base-content/60">{selected.icon}</span>
                )}
                <span className="flex-1 truncate text-left">
                    {selected?.label ?? (
                        <span className="text-base-content/40">{placeholder}</span>
                    )}
                </span>
                <ChevronDown size={14} className="shrink-0 opacity-50" />
            </label>

            <ul
                tabIndex={0}
                className="dropdown-content z-50 mt-1 rounded-xl shadow-xl
                    bg-base-100 border border-base-300
                    p-1.5 w-full min-w-[10rem] flex flex-col gap-0.5"
            >
                {options.map((opt) => {
                    const isSelected = opt.value === value;
                    return (
                        <li key={opt.value}>
                            <button
                                onClick={() => {
                                    onChange(opt.value);
                                    document.activeElement?.blur();
                                }}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm w-full text-left
                                    transition-colors duration-100
                                    ${isSelected
                                        ? "bg-primary/10 text-primary font-semibold"
                                        : "text-base-content hover:bg-base-200"
                                    }`}
                            >
                                {opt.icon && (
                                    <span className="shrink-0">{opt.icon}</span>
                                )}
                                <span className="flex-1">{opt.label}</span>
                                {isSelected && (
                                    <Check size={13} className="ml-auto shrink-0 text-primary" />
                                )}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export default SelectDropdown;
